import PromoSection from "@bmi-digital/components/promo-section";
import { graphql } from "gatsby";
import React, { useContext } from "react";
import { useTheme } from "@mui/material/styles";
import RichText from "./RichText";
import { SectionsContext } from "./Sections";
import ButtonLink from "./link/ButtonLink";
import { PromoSectionLink } from "./styles/PromoSectionStyles";
import createImageProps from "./image/createImageProps";
import createVideoProps from "./video/createVideoProps";
import type { RichTextData } from "./RichText";
import type { LinkData } from "./link/types";
import type { ImageVillain } from "./image/types";
import type { PayloadPromoSection } from "../types/payload";
import type { Category } from "./Category";
import type { VideoVillain } from "./video/types";

export type PromoSectionData = Pick<
  PayloadPromoSection,
  "blockType" | "id" | "title" | "subtitle" | "brandLogo" | "backgroundColor"
> & {
  body: RichTextData | null;
  tags:
    | readonly {
        value: Category;
      }[]
    | null;
  cta: readonly [LinkData] | null;
  featuredMedia: ImageVillain;
  featuredVideo: VideoVillain;
};

const backgroundColorMap = {
  White: "white" as const,
  Alabaster: "alabaster" as const
};

const IntegratedPromoSection = ({ data }: { data: PromoSectionData }) => {
  const { id, title, subtitle, body, cta } = data;

  const { [id]: displayProps } = useContext(SectionsContext);
  const theme = useTheme();

  return (
    <PromoSection
      title={title}
      media={
        "featuredVideo" in data && data.featuredVideo
          ? createVideoProps(data.featuredVideo, theme)
          : "featuredMedia" in data && data.featuredMedia
            ? createImageProps(
                { ...data.featuredMedia, position: "top left" },
                theme
              )
            : undefined
      }
      backgroundColor={
        displayProps
          ? backgroundColorMap[displayProps.backgroundColor ?? "White"]
          : // eslint-disable-next-line security/detect-object-injection
            backgroundColorMap[
              "backgroundColor" in data && data.backgroundColor
                ? data.backgroundColor
                : "White"
            ]
      }
      isReversed={displayProps ? displayProps.isReversed : undefined}
      data-testid="promo-section"
    >
      {body ? <RichText document={body} hasNoBottomMargin /> : subtitle}
      {cta && cta[0] && (
        <PromoSectionLink>
          <ButtonLink data={cta[0]}>{cta[0].label}</ButtonLink>
        </PromoSectionLink>
      )}
    </PromoSection>
  );
};

export default IntegratedPromoSection;

export const query = graphql`
  fragment PromoSectionFragment on PayloadPromoSection {
    id
    blockType
    title
    subtitle
    body {
      ...RichTextFragment
    }
    brandLogo
    tags {
      value {
        ...PayloadCategoryFragment
      }
    }
    cta {
      ...LinkFragment
    }
    backgroundColor
    featuredMedia {
      ...ImageVillainFragment
    }
    featuredVideo {
      ...VideoVillainFragment
    }
  }
`;
